/**
 * URL hygiene for image `src` attributes (REQ-C-047).
 *
 * The widget renders backend-supplied image URLs (e.g. a specialist's
 * `photo_url`) into an `<img src>`. An attacker-controlled or malformed
 * URL must NEVER be able to inject script (`javascript:`) or smuggle
 * inline payloads (`data:` / `blob:`). This helper restricts an image
 * URL to the `http:` / `https:` schemes only; anything else (including
 * scheme-relative `//host`, `javascript:`, `data:`, `blob:`, `file:`,
 * `vbscript:`, or an unparseable string) resolves to `null` so the
 * caller can skip rendering the image entirely.
 *
 * This is the house URL-hygiene gate for image sources. The existing
 * ListSelection `iconUrl` path assigns `img.src` with no scheme check;
 * new image-rendering paths route through here instead.
 */

/** Schemes permitted for an image `src`. */
const ALLOWED_IMAGE_SCHEMES = new Set(["http:", "https:"]);

/**
 * Return the URL unchanged if it is a syntactically valid absolute
 * http(s) URL, otherwise `null`.
 *
 * @param raw - candidate image URL from the backend (may be undefined/null)
 */
export function safeImageUrl(raw: string | null | undefined): string | null {
  if (typeof raw !== "string") return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  // `new URL` requires an absolute URL with a scheme. Scheme-relative
  // (`//host/x`), relative paths, and `javascript:`/`data:`/`blob:` all
  // either throw or resolve to a disallowed protocol below.
  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    return null;
  }
  if (!ALLOWED_IMAGE_SCHEMES.has(parsed.protocol)) return null;
  return trimmed;
}
